import { useState } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { Button, TextField } from '@trove/ui';
import { EnvelopeSimple, Key } from '@phosphor-icons/react';
import { postJson } from '../api';
import { EntryActions, EntryLayout, EntryNotice } from '../components/EntryLayout';
import { passkeysSupported, signInWithPasskey } from '../passkeys';
import { isDemoHost } from '../session';
import styles from './login.module.css';

/** Signed-in visitors are redirected in `__root`; this is a safety net if Outlet mounts. */
export const Route = createFileRoute('/login')({ component: () => <Navigate to="/" replace /> });

function authReturnTo() {
  const { pathname, search } = window.location;
  if (pathname === '/login') return '/';
  return `${pathname}${search}`;
}

function loginLinkError() {
  const reason = new URLSearchParams(window.location.search).get('error');
  if (reason === 'expired') return 'That sign-in link expired. Request a new one.';
  if (reason === 'used') return 'That sign-in link was already used. Request a new one.';
  return undefined;
}

export function LoginScreen() {
  const queryClient = useQueryClient();
  const [sentTo, setSentTo] = useState<string>();
  const [debugUrl, setDebugUrl] = useState<string>();
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [requestError, setRequestError] = useState<string>();
  const [passkeyError, setPasskeyError] = useState<string>();
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [linkError] = useState(loginLinkError);
  const showPasskey = passkeysSupported() && !isDemoHost();

  const requestLink = async (email: string) => {
    const result = await postJson<{ debugUrl?: string }>('/v1/auth/magic-link', {
      email,
      returnTo: authReturnTo(),
    });
    setDebugUrl(result.debugUrl);
  };

  const form = useForm({
    defaultValues: { email: '' },
    onSubmit: async ({ value }) => {
      setRequestError(undefined);
      try {
        await requestLink(value.email.trim());
        setResendState('idle');
        setSentTo(value.email.trim());
      } catch (cause) {
        setRequestError(cause instanceof Error ? cause.message : 'Could not send a sign-in link.');
      }
    },
  });

  const resend = async () => {
    if (!sentTo) return;
    setRequestError(undefined);
    setResendState('sending');
    try {
      await requestLink(sentTo);
      setResendState('sent');
    } catch (cause) {
      setResendState('idle');
      setRequestError(cause instanceof Error ? cause.message : 'Could not resend the link.');
    }
  };

  const onPasskey = async () => {
    setPasskeyError(undefined);
    setPasskeyPending(true);
    try {
      await signInWithPasskey();
      await queryClient.invalidateQueries({ queryKey: ['session'] });
      window.location.assign(authReturnTo());
    } catch (cause) {
      if (cause instanceof Error && cause.name === 'NotAllowedError') {
        setPasskeyError('Passkey sign-in was cancelled.');
      } else {
        setPasskeyError(cause instanceof Error ? cause.message : 'Passkey sign-in failed.');
      }
    } finally {
      setPasskeyPending(false);
    }
  };

  if (sentTo) {
    return (
      <EntryLayout
        eyebrow={
          <span className={styles.mailIcon}>
            <EnvelopeSimple size={18} />
          </span>
        }
        title="Check your email"
        description={
          <>
            We sent a sign-in link to <strong>{sentTo}</strong>. Open it on this device. It expires
            in 10 minutes.
          </>
        }
      >
        {requestError ? <EntryNotice tone="danger">{requestError}</EntryNotice> : null}
        <EntryActions>
          {debugUrl ? (
            <Button variant="primary" onClick={() => window.location.assign(debugUrl)}>
              Open local sign-in link
            </Button>
          ) : null}
          <Button disabled={resendState === 'sending'} onClick={() => void resend()}>
            {resendState === 'sending'
              ? 'Sending…'
              : resendState === 'sent'
                ? 'Sent again'
                : 'Resend link'}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setSentTo(undefined);
              setRequestError(undefined);
            }}
          >
            Use a different email
          </Button>
        </EntryActions>
      </EntryLayout>
    );
  }

  return (
    <EntryLayout
      title={
        <>
          Your life, organized.
          <br />
          Only you can read it.
        </>
      }
      description="A private, encrypted home for the files and records that matter most."
    >
      {linkError ? <EntryNotice tone="danger">{linkError}</EntryNotice> : null}
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.Field
          name="email"
          validators={{
            onBlur: ({ value }) =>
              !value || /\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email address',
            onSubmit: ({ value }) =>
              /\S+@\S+\.\S+/.test(value) ? undefined : 'Enter a valid email address',
          }}
        >
          {(field) => (
            <TextField
              label="Email"
              type="email"
              autoComplete="username webauthn"
              placeholder="you@example.com"
              value={field.state.value}
              onBlur={field.handleBlur}
              onValueChange={field.handleChange}
              error={field.state.meta.errors.join(', ')}
            />
          )}
        </form.Field>
        {requestError ? <EntryNotice tone="danger">{requestError}</EntryNotice> : null}
        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <EntryActions>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Sending link…' : 'Continue with email'}
              </Button>
            </EntryActions>
          )}
        </form.Subscribe>
      </form>
      {showPasskey ? (
        <>
          <div className={styles.divider}>
            <span>or</span>
          </div>
          <EntryActions>
            <Button disabled={passkeyPending} onClick={() => void onPasskey()}>
              <Key size={15} />
              {passkeyPending ? 'Waiting for passkey…' : 'Sign in with a passkey'}
            </Button>
          </EntryActions>
          {passkeyError ? <p className={styles.passkeyError}>{passkeyError}</p> : null}
        </>
      ) : null}
    </EntryLayout>
  );
}
